import React, {useMemo} from 'react';

import {Icon, Text} from '#component/base';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {Buttons} from '#component/page/ActionPlan/components/Buttons';
import {Graph} from '#component/page/ActionPlan/components/Graph';
import {Transition} from '#component/util';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

import {HeaderInfos, StatusListItems} from './Header.types';

import * as css from './Header.css';

const getStatusListItems = (
  duration: string,
  goals: string | number,
  categories: string,
): StatusListItems[] => [
  {
    icon: 'CalendarBlank',
    label: 'Duration',
    value: duration || '12 wks',
  },
  {
    icon: 'CheckSquareOffset',
    label: 'Goals',
    value: (goals ?? '').toString(),
  },
  {
    icon: 'PersonSimple',
    label: 'Lifestyle Categories',
    value: categories,
  },
];

const useHeaderInfos = (
  userFirstname?: string | null,
  pageState?: ActionPlanPage.State,
): HeaderInfos => {
  switch (pageState) {
    case 'inactive':
      return {
        title: userFirstname ? `${userFirstname}'s Action Plan` : 'My Action Plan',
        description:
          'Based on your latest TallyAge™ result and your recent Lifestyle Survey, we recommend you start with the following plan to maximize change',
        icon: 'ClipboardText',
      };
    case 'active':
      return {
        title: userFirstname ? `${userFirstname}'s Action Plan` : 'My Action Plan',
        description:
          'You’re one step closer to making a positive impact! Keep up with your selected goals to increase your potential for  a lower TallyAge™.',
        icon: null,
      };
    case 'high-performer':
      return {
        title: userFirstname ? `${userFirstname}'s Action Plan` : 'My Action Plan',
        description:
          'You’re a role model! Based on your latest TallyAge™ result and your recent Lifestyle Survey, we suggest you explore our Emerging Research recommendations to increase your opportunity for a positive change to your TallyAge™',
        icon: 'ClipboardText',
      };
    case 'error':
      return {
        title: 'Action Plan Unavailable',
        description:
          'We apologies for the inconvenience. Please contact support to fix this issue.',
        icon: 'ClipboardText',
      };
    case 'missing-lab':
    case 'missing-survey':
      return {
        title: 'Building your new Action Plan...',
        description:
          'We are awaiting information from your Lifestyle Survey. Our team of scientists will use this and your recent TallyAge™ result to create an updated personalized Action Plan.',
        icon: 'SpinnerGap',
      };
    case 'missing-survey-and-lab':
    case 'missing-lab-result':
      return {
        title: 'Building your Action Plan...',
        description:
          'Our team of scientists is currently creating your personalized Action Plan. We’ll notify you when it’s ready.',
        icon: 'SpinnerGap',
      };
    case 'non-member':
    default:
      return {
        title: 'Unlock Action Plan',
        description:
          'By becoming a member, you’ll unlock an actionable set of personalized lifestyle recommendations built for you by our team of longevity scientists.',
        icon: 'Lock',
      };
  }
};

export interface HeaderProps {
  userFirstname?: string | null;
  pageState: ActionPlanPage.State;
  actionPlanId?: string;
  adjustments?: ActionPlanPage.ActionPlanAdjustment[];
  deltaAge: number;
  canCheckIn?: boolean;
}
export function Header({
  userFirstname,
  pageState,
  actionPlanId,
  adjustments = [],
  deltaAge,
  canCheckIn = false,
}: HeaderProps) {
  const {isDesktop} = useViewportSize();
  const userInfo = useHeaderInfos(userFirstname, pageState);
  const selectedAdjustments = adjustments.filter(adjustment => adjustment.selected);

  const lifestyleCategories = useMemo(() => {
    const categories = selectedAdjustments.map(
      adjustment => adjustment?.healthFactorAssessment?.categoryAssessment?.category?.title,
    );
    const uniqueCategories = Array.from(new Set(categories));
    uniqueCategories.sort();
    if (uniqueCategories.length) {
      return uniqueCategories.filter(c => c).join(', ');
    } else {
      return 'No categories selected';
    }
  }, [selectedAdjustments]);

  const isActionPlanActive = pageState === 'active';

  if (!userInfo) {
    return null;
  }

  return (
    <div className={css.header({isActionPlanActive})}>
      <div
        className={sprinkles({
          display: 'flex',
          dir: 'col',
          maxW: isActionPlanActive ? ['full', 'full', 'half'] : 'full',
        })}>
        <div
          className={sprinkles({
            display: 'flex',
            items: ['start', 'center'],
            mB: 20,
            dir: ['col', 'row'],
            w: 'full',
          })}>
          {userInfo.icon && (
            <Icon
              aria-hidden
              weight="thin"
              color="burgundy"
              name={userInfo.icon}
              className={sprinkles({
                mR: 10,
                size: [48, 64],
                mB: [20, 0],
              })}
            />
          )}
          <Text as="h2" variant="heading2" color="deepBurgundy">
            {userInfo.title}
          </Text>
        </div>
        <Text as="p" color="doveGray" variant="bodyLarge" className={css.description}>
          {userInfo.description}
        </Text>
        {(!['unavailable', 'non-member'].includes(pageState) || !pageState) && (
          <div
            className={sprinkles({
              display: 'flex',
              dir: isActionPlanActive ? 'col' : 'row',
              gap: isActionPlanActive ? [0, 40] : 40,
              mT: [28, 40],
              items: isActionPlanActive ? 'start' : 'center',
            })}>
            {pageState !== 'missing-survey' && (
              <StatusList
                pageState={pageState}
                duration="12 wks"
                goals={selectedAdjustments?.length || 0}
                categories={lifestyleCategories}
              />
            )}
            <Buttons
              buttonState={isDesktop || isActionPlanActive ? pageState : 'hidden'}
              actionPlanId={actionPlanId}
              isActionPlanActive={isActionPlanActive}
              canCheckIn={canCheckIn}
            />
          </div>
        )}
      </div>
      {isActionPlanActive && (
        <Transition appear show>
          <Graph deltaAge={deltaAge} />
        </Transition>
      )}
    </div>
  );
}

interface StatusListProps {
  pageState?: ActionPlanPage.State;
  duration: string;
  goals: string | number;
  categories?: string;
}
const StatusList = ({pageState, duration, goals, categories = ''}: StatusListProps) => {
  return (
    <Text as="ul" className={css.statusList}>
      {getStatusListItems(duration, goals, categories).map(({icon, label, value}, index) => (
        <li key={index} className={css.statusListItem}>
          <Text
            color="doveGray"
            variant="label"
            transform="uppercase"
            className={sprinkles({
              display: 'flex',
              items: 'center',
              mB: 4,
            })}>
            <Icon name={icon} size="normal" color="doveGray" />
            <span
              className={sprinkles({
                display: 'flex',
                w: 'maxContent',
                mL: 8,
              })}>
              {label}
            </span>
          </Text>
          <Text color="deepBurgundy" variant="bodyLarge">
            {pageState === 'error' ? '-' : value}
          </Text>
        </li>
      ))}
    </Text>
  );
};
